import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FileText, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { GetOneExpeditionBoResponseDto } from "@/types/api/api-types";
import { DocumentListItem } from "@/components/shared/documentListItem";
import { InfoCard } from "./infoCard";
import { cn } from "@/lib/utils";
import { getDocumentSections } from "./expeditionDetail.utils";

const SECTION_DOT_COLORS = {
  preExpedition: "bg-sky-400",
  expedition: "bg-blue-600",
  postExpedition: "bg-emerald-500",
  other: "bg-gray-400",
} as const;

export const DocumentsSection = ({
  expedition,
  onEdit,
}: {
  expedition: GetOneExpeditionBoResponseDto;
  onEdit: () => void;
}) => {
  const { t } = useTranslation(["boExpeditions", "common"]);
  const sections = useMemo(() => getDocumentSections(expedition), [expedition]);

  return (
    <InfoCard
      icon={FileText}
      title={t("expeditionDrawer.documentsTitle")}
      headerRight={
        <Button
          variant="ghost"
          size="icon"
          className="size-8"
          type="button"
          title={t("expeditionDrawer.editDocuments")}
          onClick={onEdit}
        >
          <Pencil className="size-4" />
        </Button>
      }
    >
      {sections.length > 0 ? (
        <div className="flex flex-col gap-5 pb-4">
          {sections.map((section) => (
            <div key={section.key} className="flex flex-col gap-1">
              <div className="flex items-center gap-2 px-4 pb-1">
                <span className={cn("size-1.5 rounded-full", SECTION_DOT_COLORS[section.key])} />
                <p className="text-xs font-semibold tracking-wide text-gray-700 uppercase">
                  {t(`expeditionDrawer.documentSections.${section.key}`)}
                </p>
                <span className="rounded-full bg-gray-100 px-1.5 text-[10px] font-medium text-gray-500">
                  {section.documents.length}
                </span>
                <div className="h-px flex-1 bg-gray-100" />
              </div>
              {section.documents.map((file) => (
                <DocumentListItem key={file.id} file={file} showDetailsInfo />
              ))}
            </div>
          ))}
        </div>
      ) : (
        <p className="px-4 pb-4 text-sm text-gray-500">{t("expeditionDrawer.noDocuments")}</p>
      )}
    </InfoCard>
  );
};
